/**
 * Gelir-Gider / Nakit Akış ekranı (Faz 1).
 *
 * İki para havuzu mantığı (muhasebe değil, KASA mantığı):
 *  - Nakit Giderler: teker teker girilir; NAKIT + Ödendi olunca açık kasaya
 *    CashMovement(EXPENSE) yazılır (backend settle_expense_cash). Üstte
 *    bugünün nakit özeti (daily-cashier raporu). POS'tan gelen kasa
 *    hareketleri ayrı listede [POS] rozetiyle gösterilir.
 *  - Banka Giderleri: serbest/toplu satır (BANKA etiketi). Kasaya dokunmaz.
 *  - Kategoriler: standart Türk restoranı gider kalemleri (seed butonu).
 */
import { Card, Stack, Table, Badge, Tabs, Button, TextInput, SimpleGrid, Text, Group, Alert, Modal, NumberInput, Select } from "@mantine/core";
import { IconPlus, IconSearch, IconCash, IconReceipt2, IconBuildingBank, IconArrowsExchange, IconInfoCircle, IconCategory } from "@tabler/icons-react";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useResource, useInfiniteResource, useDailyCashier, apiClient, useSafeMovements, useSafeDepositFromDrawer, useSafeWithdrawToDrawer, useSafePay } from "../../../shared/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader, MoneyDisplay, EmptyState, ResourceForm, InfiniteTable, KPICard, formatDateTime, type FormField } from "../../../shared/ui";
import { useAuth } from "../../../shared/auth";

// POS kaynaklı kasa hareketi tipleri (otomatik yansıyanlar).
export function ExpensesPage() {
  const [tab, setTab] = useState<string | null>("cash");
  // "Genel Kasa" sekmesi RBAC SAFE.READ'e bağlı — varsayılan yalnız OWNER
  // görür. Owner İzin Matrisi'nden (Kullanıcılar & RBAC) başka role/kullanıcıya
  // READ verirse sekme onlarda da açılır. Backend cash-safe API'si de aynı
  // yetkiyle korunur, yani sekme gizliyken endpoint de erişilemez.
  const { permissions } = useAuth();
  const canSeeSafe = permissions.some(
    (p) => p.resource === "SAFE" && p.actions.includes("READ"),
  );
  // Yetki kalkarken "safe" sekmesi seçiliyse otomatik ilk sekmeye düş.
  const activeTab = tab === "safe" && !canSeeSafe ? "cash" : tab;

  return (
    <Stack gap="md">
      <PageHeader
        title="Gelir-Gider / Nakit Akış"
        subtitle="Giderleri buraya girin (nakit/banka). Müşteri tahsilatları için → Ödemeler, nakit kasa sayımı için → Kasa Hareketleri."
      />
      <Tabs value={activeTab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="cash" leftSection={<IconCash size={16} />}>Nakit Giderler</Tabs.Tab>
          <Tabs.Tab value="bank" leftSection={<IconBuildingBank size={16} />}>Banka Giderleri</Tabs.Tab>
          {canSeeSafe && (
            <Tabs.Tab value="safe" leftSection={<IconBuildingBank size={16} />}>Genel Kasa</Tabs.Tab>
          )}
          <Tabs.Tab value="categories" leftSection={<IconCategory size={16} />}>Kategoriler</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="cash" pt="md"><NakitGiderlerTab /></Tabs.Panel>
        <Tabs.Panel value="bank" pt="md"><BankaGiderlerTab /></Tabs.Panel>
        {canSeeSafe && (
          <Tabs.Panel value="safe" pt="md"><GenelKasaTab /></Tabs.Panel>
        )}
        <Tabs.Panel value="categories" pt="md"><KategorilerTab /></Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Sekme 1: Nakit Giderler
// ---------------------------------------------------------------------------
function NakitGiderlerTab() {
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 300);
  const { data: daily } = useDailyCashier();
  const { items, total, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteResource<any>("expenses", { search: debounced, filters: { payment_method: "NAKIT" } });
  const [opened, { open, close }] = useDisclosure(false);
  const catOptions = useCategoryOptions();

  const cashSales = daily?.cash_total_minor ?? 0;
  const cashExpenses = daily?.expenses_total_minor ?? 0;

  const EXPENSE_FIELDS: FormField[] = [
    { name: "title", label: "Başlık", required: true, placeholder: "Market alışverişi, Personel avansı..." },
    { name: "category", label: "Kategori", type: "select", required: true, options: catOptions },
    { name: "amount_minor", label: "Tutar", type: "money", required: true, min: 0 },
    {
      name: "cash_source", label: "Ödeme Kaynağı", type: "select",
      defaultValue: "DRAWER",
      options: [
        { value: "DRAWER", label: "Kasa (çekmece) — günlük küçük gider" },
        { value: "SAFE", label: "Genel Kasa — büyük ödeme (tedarikçi/maaş)" },
      ],
      description: "Büyük ödemeler genel kasadan; genel kasada yeterli yoksa engellenir.",
    },
    { name: "expense_date", label: "Gider Tarihi", type: "date", required: true, description: "Faturanın ait olduğu gün (ödeme tarihinden farklı olabilir)" },
    { name: "description", label: "Açıklama", type: "textarea" },
    { name: "is_paid", label: "Ödendi (kasadan düşülsün)", type: "switch", defaultValue: true },
  ];

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <KPICard title="Bugünkü Nakit Satış" value={<MoneyDisplay minor={cashSales} />} icon={<IconCash size={20} />} color="green" />
        <KPICard title="Bugünkü Nakit Gider" value={<MoneyDisplay minor={cashExpenses} />} icon={<IconReceipt2 size={20} />} color="orange" />
      </SimpleGrid>
      <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
        Devreden kasa ve <b>Beklenen Kasa</b> (devreden + satış − gider) için <b>Z Raporu (Gün Sonu)</b> ekranını kullanın.
        Buraya girilen nakit giderler, kasa açıksa otomatik olarak kasadan düşülür.
        Tüm kasa hareketleri için sol menüdeki <b>Kasa Hareketleri</b> sayfasını kullanın.
      </Alert>

      <TextInput
        placeholder="Başlık, açıklama veya tedarikçi ile ara..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        size="md"
      />

      <Group justify="space-between" align="center">
        <Text fw={600} size="sm">Nakit Gider Kayıtları ({total})</Text>
        <Button leftSection={<IconPlus size={16} />} onClick={open} size="sm">Yeni Nakit Gider</Button>
      </Group>
      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={items}
          rowKey={(e) => e.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          emptyContent={<EmptyState title={debounced ? "Eşleşen gider yok" : "Nakit gider kaydı yok"} />}
          columns={[
            { header: "Tarih", render: (e) => new Date(e.expense_date).toLocaleDateString("tr-TR") },
            { header: "Başlık", render: (e) => <span style={{ fontWeight: 500 }}>{e.title}</span> },
            { header: "Kategori", render: (e) => e.category_name || "-" },
            { header: "Tutar", align: "right", render: (e) => <MoneyDisplay minor={e.amount_minor || 0} fw={600} /> },
            { header: "Kasa", render: (e) => (
              e.safe_movement
                ? <Badge color="grape" variant="light">Genel kasadan</Badge>
                : e.cash_movement
                  ? <Badge color="green" variant="light">Kasadan düşüldü</Badge>
                  : <Badge color="gray" variant="light">Kasa dışı</Badge>
            ) },
            { header: "Durum", render: (e) => (e.is_paid ? <Badge color="green">Ödendi</Badge> : <Badge color="orange">Bekliyor</Badge>) },
          ]}
        />
      </Card>

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="expenses"
        title="Yeni Nakit Gider"
        fields={EXPENSE_FIELDS}
        transform={(v) => ({ ...v, payment_method: "NAKIT" })}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Sekme 2: Banka Giderleri
// ---------------------------------------------------------------------------
function BankaGiderlerTab() {
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 300);
  const { items, total, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteResource<any>("expenses", { search: debounced, filters: { payment_method: "BANKA" } });
  const [opened, { open, close }] = useDisclosure(false);
  const catOptions = useCategoryOptions();

  const BANK_FIELDS: FormField[] = [
    { name: "title", label: "Başlık", required: true, placeholder: "Elektrik, Kira, Ay sonu banka harcaması..." },
    { name: "category", label: "Kategori", type: "select", required: true, options: catOptions },
    { name: "amount_minor", label: "Tutar", type: "money", required: true, min: 0 },
    { name: "expense_date", label: "Gider Tarihi", type: "date", required: true },
    { name: "bank_label", label: "Banka Etiketi", placeholder: "Garanti TL / İş Bankası..." },
    { name: "description", label: "Açıklama", type: "textarea" },
    { name: "is_paid", label: "Ödendi", type: "switch", defaultValue: true },
  ];

  return (
    <Stack gap="md">
      <Alert variant="light" color="gray" icon={<IconInfoCircle size={16} />}>
        Banka giderleri kasaya dokunmaz. POS komisyonu / valör takibi yapılmaz — toplu/serbest satır olarak girebilirsiniz
        (ör. "Ay sonu banka harcaması 100.000 ₺").
      </Alert>
      <TextInput
        placeholder="Başlık, açıklama veya banka etiketi ile ara..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        size="md"
      />
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm">Banka Gider Kayıtları ({total})</Text>
        <Button leftSection={<IconPlus size={16} />} onClick={open} size="sm">Yeni Banka Gideri</Button>
      </Group>
      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={items}
          rowKey={(e) => e.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          emptyContent={<EmptyState title={debounced ? "Eşleşen gider yok" : "Banka gider kaydı yok"} />}
          columns={[
            { header: "Tarih", render: (e) => new Date(e.expense_date).toLocaleDateString("tr-TR") },
            { header: "Başlık", render: (e) => <span style={{ fontWeight: 500 }}>{e.title}</span> },
            { header: "Kategori", render: (e) => e.category_name || "-" },
            { header: "Banka", render: (e) => e.bank_label || "-" },
            { header: "Tutar", align: "right", render: (e) => <MoneyDisplay minor={e.amount_minor || 0} fw={600} /> },
            { header: "Durum", render: (e) => (e.is_paid ? <Badge color="green">Ödendi</Badge> : <Badge color="orange">Bekliyor</Badge>) },
          ]}
        />
      </Card>

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="expenses"
        title="Yeni Banka Gideri"
        fields={BANK_FIELDS}
        transform={(v) => ({ ...v, payment_method: "BANKA" })}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Sekme 3: Genel Kasa (Safe) — iki-katmanlı kasa
// ---------------------------------------------------------------------------
const SAFE_MOVEMENT_LABEL: Record<string, string> = {
  OPENING_BALANCE: "Açılış Bakiyesi",
  DRAWER_TO_SAFE: "Kasadan Geldi (skim)",
  SAFE_TO_DRAWER: "Kasaya Verildi (float)",
  EXPENSE: "Ödeme (gider)",
  SUPPLIER_PAYMENT: "Tedarikçi Ödemesi",
  PAYROLL: "Maaş / Avans",
  BANK_DEPOSIT: "Bankaya Yatırma",
  BANK_WITHDRAWAL: "Bankadan Çekme",
  ADJUSTMENT: "Düzeltme",
};

function GenelKasaTab() {
  const { data, isLoading } = useSafeMovements();
  const balance = data?.balance_minor ?? 0;
  const movements: any[] = data?.results || [];

  const deposit = useSafeDepositFromDrawer();
  const withdraw = useSafeWithdrawToDrawer();
  const pay = useSafePay();

  // Tek modal, üç mod: 'deposit' | 'withdraw' | 'pay'
  const [modal, setModal] = useState<null | "deposit" | "withdraw" | "pay">(null);
  const [amount, setAmount] = useState<number | "">("");
  const [payType, setPayType] = useState("SUPPLIER_PAYMENT");
  const [note, setNote] = useState("");

  function reset() {
    setModal(null);
    setAmount("");
    setNote("");
    setPayType("SUPPLIER_PAYMENT");
  }

  async function submit() {
    const amount_minor = Math.round(Number(amount) * 100);
    if (!amount_minor || amount_minor <= 0) {
      notifications.show({ message: "Geçerli bir tutar girin", color: "red" });
      return;
    }
    try {
      if (modal === "deposit") {
        await deposit.mutateAsync({ amount_minor, notes: note });
        notifications.show({ message: "Kasadan genel kasaya aktarıldı", color: "green" });
      } else if (modal === "withdraw") {
        await withdraw.mutateAsync({ amount_minor, notes: note });
        notifications.show({ message: "Genel kasadan kasaya float verildi", color: "green" });
      } else if (modal === "pay") {
        await pay.mutateAsync({ amount_minor, movement_type: payType, notes: note });
        notifications.show({ message: "Genel kasadan ödeme yapıldı", color: "green" });
      }
      reset();
    } catch (e: any) {
      const code = e?.response?.data?.code;
      notifications.show({
        title: code === "SAFE_INSUFFICIENT" ? "Yetersiz Bakiye" : "Hata",
        message: e?.response?.data?.detail || "İşlem başarısız",
        color: "red",
      });
    }
  }

  const busy = deposit.isPending || withdraw.isPending || pay.isPending;

  return (
    <Stack gap="md">
      <Alert variant="light" color="grape" icon={<IconInfoCircle size={16} />}>
        <b>Genel Kasa (Ana Kasa)</b> — büyük ödemeler (tedarikçi/maaş), devreden nakit ve
        bankaya yatırma burada yönetilir. Günlük çekmece (POS kasası) ile arasında para aktarabilirsiniz.
        Büyük bir ödeme yapılacaksa <b>çekmecede para olmasa bile</b> genel kasadan ödenir; genel
        kasada yeterli yoksa işlem engellenir.
      </Alert>

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <KPICard
          title="Genel Kasa Bakiyesi"
          value={<MoneyDisplay minor={balance} />}
          icon={<IconBuildingBank size={20} />}
          color="grape"
        />
        <Card padding="md" radius="md" withBorder>
          <Stack gap="xs">
            <Group gap="xs" wrap="wrap">
              <Button size="xs" variant="light" color="green"
                leftSection={<IconArrowsExchange size={14} />}
                onClick={() => setModal("deposit")}>
                Kasadan Aktar
              </Button>
              <Button size="xs" variant="light" color="blue"
                leftSection={<IconArrowsExchange size={14} />}
                onClick={() => setModal("withdraw")}>
                Kasaya Float Ver
              </Button>
              <Button size="xs" color="grape"
                leftSection={<IconCash size={14} />}
                onClick={() => setModal("pay")}>
                Genel Kasadan Öde
              </Button>
            </Group>
            <Text size="xs" c="dimmed">
              "Kasadan Aktar" ve "Float Ver" için açık kasa (çekmece) gerekir.
            </Text>
          </Stack>
        </Card>
      </SimpleGrid>

      <Text fw={600} size="sm">Genel Kasa Hareketleri</Text>
      <Card padding="sm" radius="md" withBorder>
        {isLoading ? (
          <Text size="sm" c="dimmed">Yükleniyor...</Text>
        ) : movements.length === 0 ? (
          <EmptyState title="Hareket yok" description="Genel kasa işlemleri burada görünür." />
        ) : (
          <Table.ScrollContainer minWidth={640}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Zaman</Table.Th>
                  <Table.Th>Tip</Table.Th>
                  <Table.Th>Not</Table.Th>
                  <Table.Th ta="right">Tutar</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {movements.slice(0, 200).map((m: any) => (
                  <Table.Tr key={m.id}>
                    <Table.Td>{formatDateTime(m.created_at)}</Table.Td>
                    <Table.Td><Badge variant="light" color="grape">{SAFE_MOVEMENT_LABEL[m.movement_type] || m.movement_type}</Badge></Table.Td>
                    <Table.Td>{m.notes || "-"}</Table.Td>
                    <Table.Td ta="right" fw={700} c={m.amount_minor >= 0 ? "green" : "red"}>
                      {m.amount_minor >= 0 ? "+" : ""}<MoneyDisplay minor={m.amount_minor} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      <Modal
        opened={modal !== null}
        onClose={reset}
        title={
          modal === "deposit" ? "Kasadan Genel Kasaya Aktar"
          : modal === "withdraw" ? "Genel Kasadan Kasaya Float Ver"
          : "Genel Kasadan Ödeme"
        }
      >
        <Stack gap="md">
          {modal === "pay" && (
            <Select
              label="Ödeme Türü"
              data={[
                { value: "SUPPLIER_PAYMENT", label: "Tedarikçi Ödemesi" },
                { value: "PAYROLL", label: "Maaş / Avans" },
                { value: "EXPENSE", label: "Gider" },
                { value: "BANK_DEPOSIT", label: "Bankaya Yatırma" },
              ]}
              value={payType}
              onChange={(v) => setPayType(v || "SUPPLIER_PAYMENT")}
            />
          )}
          <NumberInput
            label="Tutar (₺)"
            min={0}
            decimalScale={2}
            value={amount}
            onChange={(v) => setAmount(typeof v === "number" ? v : "")}
            thousandSeparator="."
            decimalSeparator=","
          />
          <TextInput
            label="Not (opsiyonel)"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={reset}>İptal</Button>
            <Button onClick={submit} loading={busy}>Onayla</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Sekme 4: Kategoriler
// ---------------------------------------------------------------------------
const CAT_FIELDS: FormField[] = [
  { name: "code", label: "Kod", required: true, placeholder: "electricity, rent..." },
  { name: "name", label: "Ad", required: true },
  { name: "description", label: "Açıklama", type: "textarea" },
  { name: "is_recurring", label: "Tekrarlayan (her ay)", type: "switch", defaultValue: false },
  { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
];

function KategorilerTab() {
  const { data: catData } = useResource<any>("expense-categories");
  const categories: any[] = catData?.results || catData || [];
  const [opened, { open, close }] = useDisclosure(false);
  const qc = useQueryClient();

  // seed-defaults bir detail=False action — sabit URL, useAction (detail=True)
  // uygun değil; doğrudan apiClient ile çağırıp cache'i invalidate ediyoruz.
  const seed = useMutation({
    mutationFn: async () =>
      (await apiClient.post("/api/v1/expense-categories/seed-defaults/")).data,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["expense-categories"] });
      notifications.show({
        message: res?.created ? `${res.created} varsayılan kategori oluşturuldu` : "Kategoriler zaten mevcut",
        color: "green",
      });
    },
    onError: (e: any) => {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    },
  });

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm">Gider Kategorileri ({categories.length})</Text>
        <Group gap="sm">
          {categories.length === 0 && (
            <Button variant="light" onClick={() => seed.mutate()} loading={seed.isPending}>Varsayılan Kategorileri Oluştur</Button>
          )}
          <Button leftSection={<IconPlus size={16} />} onClick={open} size="sm">Yeni Kategori</Button>
        </Group>
      </Group>
      <Card padding="sm" radius="md" withBorder>
        {categories.length === 0 ? (
          <EmptyState title="Kategori yok" description="Türk restoranı için hazır kategori setini tek tıkla oluşturabilirsiniz." />
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Kod</Table.Th>
                <Table.Th>Ad</Table.Th>
                <Table.Th>Tekrarlayan</Table.Th>
                <Table.Th>Aktif</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {categories.map((c: any) => (
                <Table.Tr key={c.id}>
                  <Table.Td><Badge variant="light">{c.code}</Badge></Table.Td>
                  <Table.Td>{c.name}</Table.Td>
                  <Table.Td>{c.is_recurring ? "✓" : "—"}</Table.Td>
                  <Table.Td>{c.is_active ? "✓" : "—"}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="expense-categories"
        title="Yeni Gider Kategorisi"
        fields={CAT_FIELDS}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Yardımcı: kategori seçeneklerini Select için hazırla.
// ---------------------------------------------------------------------------
function useCategoryOptions(): Array<{ value: string; label: string }> {
  const { data } = useResource<any>("expense-categories", { filters: { is_active: true } });
  const cats: any[] = data?.results || data || [];
  return cats.map((c) => ({ value: c.id, label: c.name }));
}
